import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, DestroyRef, ElementRef, HostListener, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ReviewerService } from '../../core/services/reviewer.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { ExcelExportService } from '../../core/services/excel-export.service';
import { ColumnFilter, ColumnItem, ColumnSection } from './action-plan.interfaces';

@Component({
  selector: 'app-action-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './action-plan.component.html',
  styleUrl: './action-plan.component.css',
})
export class ActionPlanComponent implements OnInit, AfterViewChecked {
  private destroyRef = inject(DestroyRef);
  private reviewerService = inject(ReviewerService);
  private elRef = inject(ElementRef);
  private userRoleService = inject(UserRoleService);
  private dateFormatSvc = inject(DateFormatService);
  private excelExportSvc = inject(ExcelExportService);

  private _reviewId: number | null = null;
  @Input() set reviewId(value: number | null) {
    this._reviewId = value;
    if (value != null) {
      this.loadActionPlans();
    }
  }
  get reviewId(): number | null { return this._reviewId; }

  @Input() isCancelled: boolean = false;
  @Input() reviewTypeId: number | null = null;
  @Input() isReadOnly: boolean = false;
  @Input() isViewMode: boolean = false;

  isLoading = false;
  isExporting = false;
  showActionPlanGrid = true;
  successToastMessage: string | null = null;

  // ── Grid data ──
  actionPlans: any[] = [];
  private defaultOrder: any[] = [];
  apSearch = '';
  apPageSize = 10;
  apCurrentPage = 1;
  apGoToPageInput = 1;

  // ── Column Selector ──
  isColumnSelectorOpen = false;
  columnSelectorSearch = '';

  // ── Sorting ──
  sortColumn = 'ActionPlanID';
  sortDirection: 'asc' | 'desc' = 'desc';

  // ── Column Filters ──
  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];
  filterableColumns = [
    // Review Basic Details
    'ReviewName', 'CurrentStatus', 'ProposedYear', 'MasterClientName', 'DTProjectName',
    'ContractNumber', 'AdditionalContractNumber', 'MarketUnit', 'CustomerRelationshipName',
    'PlanName', 'QADRecommendation', 'ClientQualityCQI', 'ClientAccountLeadGR',
    'MasterClientID', 'DeliveryCountry', 'LocationsToBeReviewed', 'IsPrimaryLocation',
    'Quarter', 'ProposedMonth', 'RCManager', 'RCSupervisor', 'RCReviewer',
    'SecondaryManager', 'SecondarySupervisor', 'SecondaryReviewer',
    // Schedule
    'AccentureOperationsSL', 'TargetForReportPublications',
    // Review Cycle
    'ActualPublicationDate', 'OverallReportingRating',
    // Action Plan
    'ActionPlanID', 'ActionPlanDescription', 'ClosedDate', 'InternalDependency', 'TargetDate',
    'ActionPlanStatus', 'RevisedTargetDate', 'LinkedIssues',
    'APRCSupervisor', 'APRCManager', 'Subscribers', 'RCComments', 'ActionRequiredBy',
    'ReviewObjective', 'Recommendation', 'BroadIssueCategoryL1', 'DetailedIssueCategoryL2',
    'RepLetterConflict', 'RepLetterQuestionsWithConflict', 'ServiceLine',
  ];
  filters: { [key: string]: ColumnFilter } = {};

  columnSections: ColumnSection[] = [
    {
      id: 'basicDetails',
      title: 'Review Basic Details',
      expanded: false,
      columns: [
        { key: 'ReviewName', label: 'Review Name', visible: false, exportWidth: 15 },
        { key: 'CurrentStatus', label: 'Current Status', visible: false, exportWidth: 13 },
        { key: 'ProposedYear', label: 'Proposed Year', visible: false, exportWidth: 13 },
        { key: 'MasterClientName', label: 'Master Client Name', visible: false, exportWidth: 18 },
        { key: 'DTProjectName', label: 'D&T Project Name', visible: false, exportWidth: 17 },
        { key: 'ContractNumber', label: 'Contract Number', visible: false, exportWidth: 15 },
        { key: 'AdditionalContractNumber', label: 'Additional Contract Number', visible: false, exportWidth: 25 },
        { key: 'MarketUnit', label: 'Market Unit', visible: false, exportWidth: 11 },
        { key: 'CustomerRelationshipName', label: 'Customer Relationship Name', visible: false, exportWidth: 25 },
        { key: 'PlanName', label: 'Plan Name', visible: false, exportWidth: 10 },
        { key: 'QADRecommendation', label: 'QAD Recommendation (Opp) / QAD Overall Status (Del)', visible: false, exportWidth: 47 },
        { key: 'ClientQualityCQI', label: 'Client Quality (CQI)', visible: false, exportWidth: 17 },
        { key: 'ClientAccountLeadGR', label: 'Client Account Lead (GR)', visible: false, exportWidth: 21 },
        { key: 'MasterClientID', label: 'Master Client ID', visible: false, exportWidth: 14 },
        { key: 'DeliveryCountry', label: 'Delivery Country', visible: false, exportWidth: 16 },
        { key: 'LocationsToBeReviewed', label: 'Locations to be Reviewed', visible: false, exportWidth: 23 },
        { key: 'IsPrimaryLocation', label: 'Is it Primary Location', visible: false, exportWidth: 25 },
        { key: 'Quarter', label: 'Quarter', visible: false, exportWidth: 8 },
        { key: 'ProposedMonth', label: 'Proposed Month', visible: false, exportWidth: 15 },
        { key: 'RCManager', label: 'R&C Manager', visible: false, exportWidth: 13 },
        { key: 'RCSupervisor', label: 'R&C Supervisor', visible: false, exportWidth: 14 },
        { key: 'RCReviewer', label: 'R&C Reviewer', visible: false, exportWidth: 13 },
        { key: 'SecondaryManager', label: 'Secondary Manager', visible: false, exportWidth: 17 },
        { key: 'SecondarySupervisor', label: 'Secondary Supervisor', visible: false, exportWidth: 19 },
        { key: 'SecondaryReviewer', label: 'Secondary Reviewer', visible: false, exportWidth: 18 },
      ],
    },
    {
      id: 'schedule',
      title: 'Schedule',
      expanded: false,
      columns: [
        { key: 'AccentureOperationsSL', label: 'Accenture Operation Service Line', visible: false, exportWidth: 21 },
        { key: 'TargetForReportPublications', label: 'Target for Report Publications', visible: false, exportWidth: 26 },
      ],
    },
    {
      id: 'reviewCycle',
      title: 'Review Cycle',
      expanded: false,
      columns: [
        { key: 'ActualPublicationDate', label: 'Actual Publication Date', visible: false, exportWidth: 20 },
        { key: 'OverallReportingRating', label: 'Overall Reporting Rating', visible: false, exportWidth: 21 },
      ],
    },
    {
      id: 'actionPlan',
      title: 'Action Plan',
      expanded: false,
      columns: [
        { key: 'ActionPlanID', label: 'Action Plan ID', visible: true, exportWidth: 13 },
        { key: 'ActionPlanDescription', label: 'Action Plan Description', visible: true, exportWidth: 20 },
        { key: 'ClosedDate', label: 'Closed Date for the Action Plan', visible: true, exportWidth: 27 },
        { key: 'InternalDependency', label: 'Internal Dependency', visible: true, exportWidth: 18 },
        { key: 'TargetDate', label: 'Target Date for Action Plan/Remediation Action', visible: true, exportWidth: 41 },
        { key: 'ActionPlanStatus', label: 'Action Plan Status', visible: true, exportWidth: 16 },
        { key: 'RevisedTargetDate', label: 'Revised Target Date', visible: true, exportWidth: 17 },
        { key: 'LinkedIssues', label: 'Linked Issue', visible: true, exportWidth: 11 },
        { key: 'APRCSupervisor', label: 'R&C Supervisor', visible: false, exportWidth: 14 },
        { key: 'APRCManager', label: 'R&C Manager', visible: false, exportWidth: 13 },
        { key: 'Subscribers', label: 'Subscribers/Notify', visible: false, exportWidth: 16 },
        { key: 'RCComments', label: 'R&C Comments', visible: false, exportWidth: 14 },
        { key: 'ActionRequiredBy', label: 'Action Required By', visible: false, exportWidth: 18 },
        { key: 'ReviewObjective', label: 'Review Objective', visible: false, exportWidth: 15 },
        { key: 'Recommendation', label: 'Recommendation', visible: false, exportWidth: 16 },
        { key: 'BroadIssueCategoryL1', label: 'Broad Issue Category (L1)', visible: false, exportWidth: 22 },
        { key: 'DetailedIssueCategoryL2', label: 'Detailed Issue Category (L2)', visible: false, exportWidth: 24 },
        { key: 'RepLetterConflict', label: 'RepLetter Conflict', visible: false, exportWidth: 16 },
        { key: 'RepLetterQuestionsWithConflict', label: 'RepLetter Questions with Conflict', visible: false, exportWidth: 29 },
        { key: 'ServiceLine', label: 'Service Line', visible: false, exportWidth: 11 },
      ],
    },
  ];

  private router = inject(Router);

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get isRCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  constructor() {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
  }

  viewActionPlan(id: string | number): void {
    const isGoLive = this.reviewTypeId === 2;
    const urlTree = this.router.createUrlTree(['/action-plan', id], {
      queryParams: { reviewId: this.reviewId, viewOnly: 'true', fromViewIcon: '1', isGoLive: isGoLive ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  editActionPlan(ap: any): void {
    const status = ap.ActionPlanStatus?.toLowerCase() ?? '';
    const viewOnly = status === 'closed' || status === 'closed-blue not tracked';
    const isGoLive = this.reviewTypeId === 2;
    const urlTree = this.router.createUrlTree(['/action-plan', ap.ActionPlanID], {
      queryParams: { reviewId: this.reviewId, viewOnly: viewOnly ? 'true' : 'false', isGoLive: isGoLive ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  showDeleteModal = false;
  isDeletingActionPlan = false;
  private pendingDeleteItem: any = null;

  openDeleteModal(ap: any): void {
    if (!this.isAdmin) return;
    this.pendingDeleteItem = ap;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.pendingDeleteItem = null;
  }

  closeSuccessToast(): void {
    this.successToastMessage = null;
    document.body.style.overflow = '';
  }

  confirmDeleteActionPlan(): void {
    if (!this.pendingDeleteItem || this.isDeletingActionPlan) return;
    this.showDeleteModal = false;
    this.isDeletingActionPlan = true;
    const ap = this.pendingDeleteItem;
    const loggedUserId = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];
    this.reviewerService.deleteActionPlan(this.reviewId!, ap.ActionPlanID, loggedUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.actionPlans = this.actionPlans.filter(a => a !== ap);
          this.defaultOrder = this.defaultOrder.filter(a => a !== ap);
          this.isDeletingActionPlan = false;
          this.pendingDeleteItem = null;
          this.successToastMessage = 'Action plan deleted successfully';
          document.body.style.overflow = 'hidden';
          setTimeout(() => { this.successToastMessage = null; document.body.style.overflow = ''; }, 5000);
        },
        error: (err) => {
          console.error('Delete action plan error:', err);
          this.isDeletingActionPlan = false;
          this.pendingDeleteItem = null;
        },
      });
  }

  ngOnInit(): void {
    this.reviewerService.actionPlanRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadActionPlans());

    const apUpdatedHandler = (e: StorageEvent) => {
      if (e.key === 'ast_ap_updated') this.loadActionPlans();
    };
    window.addEventListener('storage', apUpdatedHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', apUpdatedHandler));
  }

  ngAfterViewChecked(): void {
    (this.elRef.nativeElement as HTMLElement)
      .querySelectorAll<HTMLElement>('.th-label')
      .forEach(el => { el.title = el.textContent?.trim() ?? ''; });
  }

  loadActionPlans(): void {
    if (this.reviewId == null) return;
    this.isLoading = true;
    this.reviewerService.getActionPlansEAV(this.reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response)
            ? response
            : (response?.actionPlans ?? response?.ActionPlans ?? response?.data ?? []);
          const raw = Array.isArray(list) ? list : [];
          this.actionPlans = raw.map((ap: any) => {
            const issues: any[] = Array.isArray(ap.IssueFields) ? ap.IssueFields : [];
            const joinField = (key: string) =>
              [...new Set(issues.map((i: any) => String(i[key] ?? '')).filter(Boolean))].join(', ');
            const issue = issues.length ? issues[0] : {};
            return {
            ReviewName: ap.ReviewName ?? '',
            CurrentStatus: ap.CurrentStatus ?? '',
            ProposedYear: ap.Review_ProposedYear ?? '',
            MasterClientName: ap.Review_ClientName ?? '',
            DTProjectName: ap.Review_ProjectName ?? '',
            ContractNumber: ap.Review_ContractNumber ?? '',
            AdditionalContractNumber: ap.Review_AdditionalContractNumber ?? '',
            MarketUnit: ap.Review_MarketUnit ?? '',
            CustomerRelationshipName: ap.CustomerRelationshipName ?? '',
            PlanName: ap.Review_PlanName ?? '',
            QADRecommendation: ap.QADRecommendation ?? '',
            ClientQualityCQI: ap.ClientQualityCQI ?? '',
            ClientAccountLeadGR: ap.ClientAccountLead ?? '',
            MasterClientID: ap.MasterClientID ?? '',
            DeliveryCountry: ap.Review_DeliveryCountry ?? '',
            LocationsToBeReviewed: ap.Review_LocationToBeReviewed ?? '',
            IsPrimaryLocation: (ap.Review_IsParentLocation === '1' || ap.Review_IsParentLocation === 1) ? 'Yes' : 'No',
            Quarter: ap.Review_Quarter ?? '',
            ProposedMonth: ap.Review_ProposedMonth ?? '',
            RCManager: ap.Review_RCManager ?? '',
            RCSupervisor: ap.Review_RCSupervisor ?? '',
            RCReviewer: ap.Review_RCReviewer ?? '',
            SecondaryManager: ap.Review_secondaryRCManager ?? ap.Review_SecondaryRCManager ?? ap.Review_SecondaryManager ?? '',
            SecondarySupervisor: ap.Review_secondaryRCSupervisor ?? ap.Review_SecondaryRCSupervisor ?? ap.Review_SecondarySupervisor ?? '',
            SecondaryReviewer: ap.Review_secondaryRCReviewer ?? ap.Review_SecondaryRCReviewer ?? ap.Review_SecondaryReviewer ?? '',
            AccentureOperationsSL: ap.Schedule_AccentureOperationsServiceLine ?? ap.Schedule_ServiceLine ?? '',
            TargetForReportPublications: this.toDisplayDate(ap.Schedule_TargetPublicationDate ?? ap.Schedule_TargetForReportPublication ?? ''),
            ActualPublicationDate: this.toDisplayDate(ap.ActualPublicationDate ?? ''),
            OverallReportingRating: ap.OverallReportingRating ?? '',
            ActionPlanID: ap.ActionPlanID ?? '',
            ActionPlanDescription: ap.Description || ap.AP_Description || ap.ActionPlanDescription || '',
            ClosedDate: this.toDisplayDate(ap.ClosedDate || ap.AP_ClosedDate || ''),
            InternalDependency: ap['Internal dependency'] || ap.InternalDependency || ap.AP_InternalDependency || '',
            TargetDate: this.toDisplayDate(ap.TargetDate || ap.TargetDateForActionPlanRemediationAction || ap.AP_TargetDate || ''),
            ActionPlanStatus: ap.Status || ap.AP_Status || ap.ActionPlanStatus || '',
            RevisedTargetDate: this.toDisplayDate(ap.RevisedTargetDate || ap.AP_RevisedTargetDate || ''),
            LinkedIssues: (ap.LinkedIssueIDs ?? '').split(',').map((id: string) => id.trim()).filter(Boolean).map((id: string) => 'I-' + id).join(', ') || (ap.LinkedIssueNames ?? ''),
            APRCSupervisor:  ap.Review_RCSupervisor ?? issue.RCSupervisor ?? ap.ActionPlanRCSupervisor ?? ap.RCSupervisor ?? '',
            APRCManager:  ap.Review_RCManager ?? issue.RCManager ?? ap.ActionPlanRCManager ?? ap.RCManager ?? '',
            Subscribers: issue.SubscriberNames ?? issue.Subscribers ?? ap.Subscribers ?? '',
            RCComments: issue.RCComments ?? ap.RCComments ?? '',
            ActionRequiredBy: issue.ActionRequiredBy ?? ap.ActionRequiredBy ?? '',
            ReviewObjective: issue.ReviewObjective ?? ap.ReviewObjective ?? '',
            Recommendation: issue.Recommendation ?? ap.Recommendation ?? '',
            BroadIssueCategoryL1: issue.BroadIssueCategoryL1 ?? ap.BroadIssueCategoryL1 ?? '',
            DetailedIssueCategoryL2: issue.DetailedIssueCategoryL2 ?? ap.DetailedIssueCategoryL2 ?? '',
            RepLetterConflict: (() => {
              const v = String(issue.RepLetterConflict ?? ap.RepLetterConflict ?? '');
              const lv = v.toLowerCase();
              if (lv === 'true'  || lv === 'yes') return 'Yes';
              if (lv === 'false' || lv === 'no')  return 'No';
              if (lv === 'not applicable') return 'Not Applicable';
              return v;
            })(),
            RepLetterQuestionsWithConflict: issue['RepLetter Questions with Conflict'] ?? issue.RepLetterQuestions ?? issue.RepLetterQuestionsWithConflict ?? ap.RepLetterQuestionsWithConflict ?? '',
            ServiceLine: joinField('ServiceLine') || joinField('Accenture Operation Service Line'),
            };
          });
          this.defaultOrder = this.sortRows([...this.actionPlans], 'ActionPlanID', 'desc');
          this.isLoading = false;
        },
        error: (err) => {
          console.error('GetActionPlans error status:', err?.status, 'message:', err?.message);
          this.isLoading = false;
        },
      });
  }

  private toDisplayDate(value: string): string {
    return this.dateFormatSvc.format(value);
  }

  private readonly DEFAULT_AP_KEYS = new Set([
    'ActionPlanID', 'ActionPlanDescription', 'ClosedDate', 'InternalDependency',
    'TargetDate', 'ActionPlanStatus', 'RevisedTargetDate', 'LinkedIssues',
  ]);

  get tableMinWidth(): string {
    const extraCount = this.columnSections.reduce((n, s) =>
      n + s.columns.filter(c => c.visible && !this.DEFAULT_AP_KEYS.has(c.key)).length, 0
    );
    return extraCount > 0 ? `calc(100% + ${extraCount * 200}px)` : '';
  }

  get filteredActionPlans(): any[] {
    const term = this.apSearch.trim().toLowerCase();
    const visibleKeys = this.columnSections
      .flatMap(s => s.columns)
      .filter(c => c.visible)
      .map(c => c.key);
    let list: any[] = term
      ? this.actionPlans.filter(ap =>
          visibleKeys.some(col => String(ap[col] ?? '').toLowerCase().includes(term)),
        )
      : [...this.actionPlans];

    list = list.filter(row =>
      this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        return this.matchOperator(String(row[col] ?? ''), f.op1, f.val1);
      }),
    );

    if (this.sortColumn) {
      const col = this.sortColumn;
      const dir = this.sortDirection;
      list = this.sortRows(list, col, dir);
    }
    return list;
  }

  get totalApPages(): number {
    return Math.max(1, Math.ceil(this.filteredActionPlans.length / this.apPageSize));
  }

  get apPageNumbers(): number[] {
    return Array.from({ length: this.totalApPages }, (_, i) => i + 1);
  }

  get pagedActionPlans(): any[] {
    const start = (this.apCurrentPage - 1) * this.apPageSize;
    return this.filteredActionPlans.slice(start, start + this.apPageSize);
  }

  goToApPage(): void {
    const p = parseInt(String(this.apGoToPageInput), 10);
    if (!isNaN(p) && p >= 1 && p <= this.totalApPages) {
      this.apCurrentPage = p;
    }
  }

  toggleColumnSelector(): void {
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
    if (this.isColumnSelectorOpen) {
      this.columnSelectorSearch = '';
    }
  }

  closeColumnSelector(): void {
    this.isColumnSelectorOpen = false;
  }

  toggleSection(section: ColumnSection): void {
    section.expanded = !section.expanded;
  }

  filteredSectionColumns(section: ColumnSection): ColumnItem[] {
    const term = this.columnSelectorSearch.trim().toLowerCase();
    if (!term) return section.columns;
    return section.columns.filter(c => c.label.toLowerCase().includes(term));
  }

  isColumnVisible(key: string): boolean {
    for (const section of this.columnSections) {
      const col = section.columns.find(c => c.key === key);
      if (col) return col.visible;
    }
    return true;
  }

  totalColumnCount(): number {
    return this.columnSections.reduce((sum, s) => sum + s.columns.length, 0);
  }

  visibleColumnCount(): number {
    return this.columnSections.reduce((sum, s) => sum + s.columns.filter(c => c.visible).length, 0);
  }

  private exportableColumns(): { key: string; header: string; width?: number }[] {
    return this.columnSections
      .flatMap(section => section.columns)
      .map(column => ({ key: column.key, header: column.label, width: column.exportWidth }));
  }

  private exportData(): any[] {
    if (!this.defaultOrder.length) return this.actionPlans;
    const currentRows = new Set(this.actionPlans);
    const savedRows = new Set(this.defaultOrder);
    return [
      ...this.defaultOrder.filter(row => currentRows.has(row)),
      ...this.actionPlans.filter(row => !savedRows.has(row)),
    ];
  }

  private sortRows(rows: any[], column: string, direction: 'asc' | 'desc'): any[] {
    return rows.sort((a, b) => {
      let va: any = a[column];
      let vb: any = b[column];
      if (['ClosedDate', 'TargetDate', 'RevisedTargetDate', 'ActualPublicationDate'].includes(column)) {
        va = this.parseDateForSort(va);
        vb = this.parseDateForSort(vb);
      } else {
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
      }
      if (va < vb) return direction === 'asc' ? -1 : 1;
      if (va > vb) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  async exportAll(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      await this.excelExportSvc.exportToExcel(
        this.exportableColumns(),
        this.exportData(),
        this.excelExportSvc.buildFileName('ActionPlan_ExportAll'),
        'Action Plan Export All',
      );
    } catch (error) {
      console.error('Action Plan Export All failed:', error);
    } finally {
      this.isExporting = false;
    }
  }

  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const visibleKeys = new Set(
        this.columnSections.flatMap(section => section.columns)
          .filter(column => column.visible)
          .map(column => column.key),
      );
      await this.excelExportSvc.exportToExcel(
        this.exportableColumns().filter(column => visibleKeys.has(column.key)),
        this.exportData(),
        this.excelExportSvc.buildFileName('ActionPlan_ExportVisible'),
        'Action Plan Export Visible',
      );
    } catch (error) {
      console.error('Action Plan Export Visible failed:', error);
    } finally {
      this.isExporting = false;
    }
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.apCurrentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  private parseDateForSort(dateStr: string): number {
    if (!dateStr) return 0;
    const digits = dateStr.replace(/-/g, '');
    const n = parseInt(digits, 10);
    return isNaN(n) ? 0 : n;
  }

  toggleColumnFilter(col: string, event: Event): void {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }

  applyFilter(): void {
    this.activeFilterColumn = null;
    this.apCurrentPage = 1;
  }

  resetFilter(col: string): void {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.apCurrentPage = 1;
  }

  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }

  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any): void {
    const clickedInsideTable = (event.target as HTMLElement).closest('table');
    if (!clickedInsideTable) {
      this.activeFilterColumn = null;
    }
  }

  setTooltipIfTruncated(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    el.title = el.scrollWidth > el.clientWidth ? (el.textContent?.trim() ?? '') : '';
  }
}
